/**
 * Shared look of the free calculators (T1051; template: the /pay-calculator design of T1039). Generic names
 * (`calc*`) so the pay and GST calculators can use one copy once both are merged. Tokens only: the input panel sits
 * on the neutral card, the answer on a success tint mixed from existing tokens, the answer figure in --success.
 * Design: docs/design/html/flows/web-landing-tool-gst.mjs (`.calc-*` in the flow CSS).
 */
import { Keyboard, type LucideIcon } from "lucide-react"
import { forwardRef, type ReactNode } from "react"

import { useLocale } from "@/core/i18n/locale-context"

/** Two panels side by side from md (stretched to the same height), stacked on phones (input first). */
export const calcPanels = "grid items-stretch gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]"

/** Input panel: neutral card surface; a flex column so the last rows can sit at the bottom. */
export const calcInputPanel =
  "bg-card border-border flex flex-col gap-3.5 rounded-xl border p-4 md:p-6"

/** Result panel: success tint over the card colour, so it stays opaque on any page background. */
export const calcResultPanel =
  "border-success/30 flex flex-col gap-3 rounded-xl border bg-[color-mix(in_oklab,var(--success)_8%,var(--card))] p-4 md:p-6"

/** Card that holds a visual under the panels. */
export const calcVisualCard =
  "bg-card border-border grid gap-3.5 rounded-xl border p-4 md:px-6 md:py-5"

/** Slot that holds the answer figure at a fixed height, so an update never moves the layout. */
export const calcResultSlot = "flex min-h-12 items-center overflow-hidden md:min-h-15"

/** The answer figure in the result accent colour. */
export const calcResultFigure =
  "text-success text-3xl leading-none sm:text-4xl font-bold tracking-tight tabular-nums md:text-5xl"

/** The one amount that drives the tool: 56 px tall with 26 px semibold numerals. */
export const calcHeroInput =
  "h-14 w-full min-w-0 bg-transparent text-[1.625rem] font-semibold tabular-nums outline-none"

/** A value change slides up 8 px and fades in (transform/opacity only); off under prefers-reduced-motion. */
export const calcEnter = (animate: boolean) =>
  animate
    ? "animate-in fade-in slide-in-from-bottom-2 duration-200 ease-out motion-reduce:animate-none"
    : ""

/** Wording of the shared shortcuts dialog (the lists of shortcuts come from each calculator). */
const SHORTCUTS_TEXT = {
  en: {
    title: "Keyboard shortcuts",
    note: "Arrow keys work while the calculator has focus; left and right while a mode option has focus.",
  },
  zh: {
    title: "键盘快捷键",
    note: "计算器获得焦点时，方向键可用；焦点在某个模式选项上时，可用左右方向键切换。",
  },
} as const

const kbdClass =
  "border-border bg-background text-foreground rounded-[5px] border px-1.5 py-px font-mono text-xs"

export const Kbd = ({ children }: { children: ReactNode }) => (
  <kbd className={kbdClass}>{children}</kbd>
)

export type ChipTone = "primary" | "success" | "muted"
const CHIP_TONES: Record<ChipTone, string> = {
  primary: "bg-primary/10 text-primary",
  success: "bg-success/15 text-success",
  muted: "bg-muted text-muted-foreground",
}

/** Small decorative icon tile; the text beside it carries the meaning. */
export const CalcIconChip = ({ icon: Icon, tone }: { icon: LucideIcon; tone: ChipTone }) => (
  <span
    aria-hidden="true"
    className={`grid size-7 shrink-0 place-items-center rounded-md ${CHIP_TONES[tone]}`}
  >
    <Icon className="size-4" />
  </span>
)

/** Panel heading with its icon chip; the wording stays plain text. */
export const CalcHeading = ({
  icon,
  tone,
  children,
}: {
  icon: LucideIcon
  tone: ChipTone
  children: ReactNode
}) => (
  <h3 className="flex items-center gap-2.5 text-lg font-semibold">
    <CalcIconChip icon={icon} tone={tone} />
    {children}
  </h3>
)

export interface CalcShortcut {
  keys: string[]
  /** Shown between the keys: "+" for a chord, "/" for alternatives. */
  separator?: "+" | "/"
  action: string
}

interface CalcShortcutsProps {
  id: string
  shortcuts: readonly CalcShortcut[]
  open: boolean
  onToggle: () => void
}

/**
 * Keyboard button plus a non-modal list of the shortcuts. The calculator root owns the keys ("?" opens, Esc
 * closes and returns focus to this button, focus leaving the calculator closes it); nothing listens on document.
 */
export const CalcShortcuts = forwardRef<HTMLButtonElement, CalcShortcutsProps>(
  function CalcShortcuts({ id, shortcuts, open, onToggle }, buttonRef) {
    const text = SHORTCUTS_TEXT[useLocale()]
    return (
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          aria-label={text.title}
          aria-expanded={open}
          aria-controls={open ? id : undefined}
          aria-keyshortcuts="?"
          onClick={onToggle}
          className="border-border bg-background text-muted-foreground hover:text-foreground focus-visible:ring-ring grid size-11 place-items-center rounded-md border outline-none focus-visible:ring-[3px]"
        >
          <Keyboard className="size-5" aria-hidden="true" />
        </button>
        {open && (
          <div
            id={id}
            role="dialog"
            aria-labelledby={`${id}-h`}
            tabIndex={-1}
            data-calc-shortcuts=""
            className="bg-popover text-popover-foreground border-border animate-in fade-in zoom-in-95 focus-visible:ring-ring absolute top-13 right-0 z-20 grid w-[min(20rem,calc(100vw-3rem))] origin-top-right gap-2.5 rounded-lg border p-3.5 shadow-lg duration-150 outline-none focus-visible:ring-2 motion-reduce:animate-none"
          >
            <b id={`${id}-h`}>{text.title}</b>
            <dl className="grid gap-2 text-sm">
              {shortcuts.map((s) => (
                <div key={s.action} className="grid grid-cols-[7rem_1fr] items-center gap-2">
                  <dt className="flex items-center gap-1">
                    {s.keys.map((k, n) => (
                      <span key={k} className="flex items-center gap-1">
                        {n > 0 && (
                          <span className="text-muted-foreground">{s.separator ?? "+"}</span>
                        )}
                        <Kbd>{k}</Kbd>
                      </span>
                    ))}
                  </dt>
                  <dd>{s.action}</dd>
                </div>
              ))}
            </dl>
            <span className="text-muted-foreground text-sm">{text.note}</span>
          </div>
        )}
      </div>
    )
  },
)
